import type { Owner } from '@paintscout/api'
import React, { useState } from 'react'
import type { AvatarProps } from '../Avatar/Avatar'
import Avatar from '../Avatar/Avatar'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { StyleClasses } from '../../styles'
import { formatName } from '@paintscout/util/builder'
import { Tooltip, useUsers } from '../..'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import classNames from 'classnames'

const useStyles = makeStyles<Theme, EstimatorAvatarProps>(
  () => ({
    img: {
      borderRadius: '50%',
      objectFit: 'cover',
      height: ({ height }) => `${height || '28px'} !important`,
      width: ({ width }) => `${width || '28px'} !important`,
      marginRight: '0px !important'
    },
    avatar: {
      padding: '0px !important',
      boxShadow: ({ borderColor }) =>
        borderColor ? `0 0 0 2px white, 0 0 0 4px ${borderColor} !important` : undefined,
      transform: 'scale(1.0) !important'
    }
  }),
  { name: 'EstimatorAvatar' }
)

export interface EstimatorAvatarProps extends AvatarProps {
  classes?: StyleClasses<typeof useStyles>
  userPictures: {
    [key: string]: string
  }
  owner?: Owner
  borderColor?: string
  hideTooltip?: boolean
}

export default function EstimatorAvatar(props: EstimatorAvatarProps) {
  const { userPictures, owner, borderColor, hideTooltip, ...avatarProps } = props
  const classes = useStyles(props)
  const userPicture = owner?.id ? userPictures?.[addAuth0Prefix(owner.id)] : null
  const userName = owner?.id ? formatName(owner) : 'PaintScout User'
  const { users } = useUsers()

  const [imgLoading, setImgLoading] = useState(false)
  const color = users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(owner?.id))?.user_metadata
    ?.color

  const avatarColor = borderColor ? borderColor : props.avatarColor ? props.avatarColor : color

  const Picture = userPicture ? (
    <img
      onClick={props.onClick}
      className={classNames(classes.img, 'disable-click')}
      src={userPicture}
      style={{
        opacity: imgLoading ? 0 : 1,
        transition: 'opacity 0.2s ease-in',
        boxShadow: borderColor
          ? `0 0 0 2px white, 0 0 0 4px ${borderColor}` // White outline followed by colored outline
          : undefined
      }}
      alt={userName}
      onLoad={() => {
        setImgLoading(false)
      }}
    />
  ) : (
    <Avatar
      className={classNames(classes.avatar, 'disable-click')}
      backgroundColor="#FFFFFF"
      avatarColor={avatarColor}
      disableTooltip
      filled
      width="28px"
      height="28px"
      fontSize={'12px'}
      {...avatarProps}
    >
      {/* Limit the username to two words so we only get two initials */}
      {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
    </Avatar>
  )

  if (hideTooltip) {
    return Picture
  }
  return (
    <Tooltip hideIcon content={userName} enterDelay={500} enterNextDelay={500}>
      {Picture}
    </Tooltip>
  )
}
